/**
 * Reads Legal Reviews › Disclaimer Options once per session so the disclaimer uses legal's own
 * wording (disclaimerLegal.ts, 2026-10-28). Returns a version string that changes when the
 * table has been read — put it in the deps of any useMemo that builds disclaimer text, so the
 * preview redraws with legal's copy.
 *
 * Submit calls `resolveLegalOptions` first, so what gets filed never depends on how fast the
 * request was submitted (the rule from Farhan's review of #396). If the read fails or times
 * out, the bundled copy is used — the same text the app has shipped since 2026-10-07.
 */
import { useEffect, useState } from "react";
import { getDisclaimerOptions } from "./api";
import { loadLegalOptions, legalLoadedAt, onLegalOptions } from "./disclaimerLegal";

export function useLegalDisclaimers(enabled = true): string {
  const [ver, setVer] = useState(legalLoadedAt());
  useEffect(() => {
    if (!enabled) return;
    const off = onLegalOptions(() => setVer(legalLoadedAt()));
    loadLegalOptions(getDisclaimerOptions).then(() => setVer(legalLoadedAt()));
    return off;
  }, [enabled]);
  return ver;
}

/** at submit: wait for the table (up to `timeoutMs`). Never throws. */
export async function resolveLegalOptions(timeoutMs = 8000): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      loadLegalOptions(getDisclaimerOptions),
      new Promise<void>((res) => { timer = setTimeout(res, timeoutMs); }),
    ]);
  } catch {
    /* bundled copy */
  } finally {
    if (timer) clearTimeout(timer);
  }
}
